import { Button, EnrollmentHelpText } from '@/shared/components'
import { StepHeader } from '../StepHeader'
import { BirthDateSection } from './eligibilityForm/BirthDateSection'
import { CitizenshipSection } from './eligibilityForm/CitizenshipSection'
import { EligibilityIdentityFields } from './eligibilityForm/EligibilityIdentityFields'
import { useEligibilityFormControls } from './eligibilityForm/useEligibilityFormControls'
import { useEligibilityFormStep } from './eligibilityForm/useEligibilityFormStep'
import type { EligibilityFormData } from './types'
import type { EnrollmentFieldEditability } from '../../utils/enrollmentHydration'

type EligibilityFormStepProps = {
  currentStep: number
  totalSteps: number
  fieldEditability: EnrollmentFieldEditability
  formData: EligibilityFormData
  updateFormData: (data: Partial<EligibilityFormData>) => void
  onBack: () => void
  onContinue: () => void
  isSubmitting?: boolean
  errorMessage?: string | null
}

export function EligibilityFormStep({
  currentStep,
  totalSteps,
  fieldEditability,
  formData,
  updateFormData,
  onBack,
  onContinue,
  isSubmitting = false,
  errorMessage
}: EligibilityFormStepProps) {
  const isDateOfBirthDisabled = fieldEditability.dateOfBirth === false
  const isCitizenshipDisabled = fieldEditability.usCitizen === false
  const isGenderDisabled = fieldEditability.gender === false
  const isEthnicityDisabled = fieldEditability.ethnicity === false
  const isEducationLevelDisabled = fieldEditability.educationLevel === false

  const {
    fieldErrors,
    genderOptions,
    ethnicityOptions,
    educationOptions,
    maxBirthDate,
    birthDateValue,
    isUnder18,
    handleBirthDatePickerChange,
    handleContinueClick,
    clearFieldError
  } = useEligibilityFormStep({
    formData,
    updateFormData,
    onContinue
  })
  const {
    handleCitizenshipChange,
    handleEducationLevelChange,
    handleEthnicityChange,
    handleGenderChange,
    isContinueDisabled
  } = useEligibilityFormControls({
    clearFieldError,
    formData,
    isSubmitting,
    isUnder18,
    updateFormData
  })

  return (
    <div className="bg-white px-4 py-2">
      <div className="mx-auto max-w-4xl animate-fade-in-up">
        <StepHeader
          currentStep={currentStep}
          totalSteps={totalSteps}
          onBack={onBack}
          showBackButton={true}
        />

        <div className="mx-auto max-w-2xl">
          <div className="flex items-start justify-center animate-fade-in-up">
            <h1 className="modal-heading__title text-center">Eligibility Information</h1>
          </div>

          <div className="mx-auto max-w-[42rem] pt-6 flex flex-col gap-4">
            <BirthDateSection
              value={birthDateValue}
              errorMessage={fieldErrors.dateOfBirth}
              maxBirthDate={maxBirthDate}
              isUnder18={isUnder18}
              disabled={isDateOfBirthDisabled}
              onChange={handleBirthDatePickerChange}
            />

            <CitizenshipSection
              value={formData.isCitizen}
              errorMessage={fieldErrors.isCitizen || errorMessage}
              disabled={isCitizenshipDisabled}
              onChange={handleCitizenshipChange}
            />

            <EligibilityIdentityFields
              gender={formData.gender}
              ethnicity={formData.ethnicity}
              educationLevel={formData.educationLevel}
              genderOptions={genderOptions}
              ethnicityOptions={ethnicityOptions}
              educationOptions={educationOptions}
              fieldErrors={fieldErrors}
              isGenderDisabled={isGenderDisabled}
              isEthnicityDisabled={isEthnicityDisabled}
              isEducationLevelDisabled={isEducationLevelDisabled}
              onGenderChange={handleGenderChange}
              onEthnicityChange={handleEthnicityChange}
              onEducationLevelChange={handleEducationLevelChange}
            />

            <div className="animate-fade-in-up" style={{ animationDelay: '340ms' }}>
              <Button
                type="button"
                onClick={handleContinueClick}
                disabled={isContinueDisabled}
                fullWidth
                className="hover:-translate-y-0.5 active:scale-95"
              >
                {isSubmitting ? 'Saving...' : 'Continue'}
              </Button>
            </div>

            {/* <div className="animate-fade-in-up" style={{ animationDelay: '380ms' }}>
            <Button type="button" variant="secondary" fullWidth className="hover:-translate-y-0.5 active:scale-95">
              Save Your Progress
            </Button>
          </div> */}

            <div className="text-center animate-fade-in-up" style={{ animationDelay: '460ms' }}>
              <EnrollmentHelpText />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
